// apps/web/src/features/admin/automations/panel/BranchesSection.tsx
import { useState } from 'react';
import type { Automation, AutomationBranch, AutomationBranchCheck } from '@capture/shared';
import { ConfirmModal } from '@/components/ConfirmModal';
import type { ConditionLookupTable } from '../../components/ConditionRow';
import type { FieldDefinitionSummary } from '../../hooks/useJobTypeFields';
import { SectionCard } from './SectionCard';
import { BranchCard } from './BranchCard';
import type { RowCheckGroupOption } from '../CheckConfigBody';
import { defaultConfigForEffect } from '../effect-bucket';
import styles from './panel.module.css';

interface PipelineStepRow {
  stepType: string;
  label: string;
}

interface Props {
  automation: Automation;
  onChange: (next: Automation) => void;
  fields: FieldDefinitionSummary[];
  /** P2b — line-item groups on the form, threaded to the row_check editor. */
  lineItemGroups?: RowCheckGroupOption[];
  lookupTables?: ConditionLookupTable[];
  /** P8 — job-level constant names, forwarded to the branch condition editor. */
  constants?: string[];
  roles?: { slug: string; name: string }[];
  /** The tenant's active users — notify's target-user picker (Task 13). */
  users?: { id: string; name: string; email: string }[];
  /** E-054 P5 follow-up — the job's form classifications, for the reclassify
   *  direct-target picker. */
  classifications?: { key: string; label: string }[];
  pipelineSteps?: PipelineStepRow[];
}

function newCheckBranch(): AutomationBranchCheck {
  return {
    family: 'check',
    effectKey: 'validate_check',
    severity: 'warning',
    config: defaultConfigForEffect('validate_check'),
  };
}

function branchesSummary(branches: AutomationBranch[]): string {
  return `${branches.length} branch${branches.length === 1 ? '' : 'es'}`;
}

/**
 * N × (IF → THEN) branch editor (design §2/§5). Every branch fires
 * independently — there's no "else"; "add another if/then" always appends a
 * fresh check branch (always → warn), matching the intent-stepper's default.
 * Branch delete goes through ConfirmModal since it's destructive and has no
 * undo.
 */
export function BranchesSection({ automation, onChange, fields, lineItemGroups = [], lookupTables = [], constants = [], roles = [], users = [], classifications = [], pipelineSteps = [] }: Props) {
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  // Pristine tracking is local-only — it must never reach the Automation
  // model (compile/save must not see it). A freshly-added branch (index
  // flagged true) offers every outcome chip until the first click commits a
  // family and clears the flag (design: "family is locked by the FIRST
  // outcome chosen"). Parallel array to automation.branches, indexed
  // POSITIONALLY (same as the `key={index}` below) — kept in sync ONLY
  // because this component's two mutations are append-at-end (addBranch)
  // and filter-by-index (confirmRemove), both of which preserve every
  // surviving branch's position. Builder follow-ups (item c) — this is a
  // delete-only-safe invariant: if a future change adds branch REORDER or
  // DUPLICATE, positional sync breaks silently (pristineFlags would point at
  // the wrong branch after either mutation) unless branches first migrate to
  // stable per-branch ids/keys and pristineFlags is rekeyed by id instead of
  // array index.
  const [pristineFlags, setPristineFlags] = useState<boolean[]>(() => automation.branches.map(() => false));

  function updateBranch(index: number, next: AutomationBranch) {
    const branches = automation.branches.slice();
    branches[index] = next;
    onChange({ ...automation, branches });
  }

  function addBranch() {
    onChange({ ...automation, branches: [...automation.branches, newCheckBranch()] });
    setPristineFlags((flags) => [...flags, true]);
  }

  function clearPristine(index: number) {
    setPristineFlags((flags) => {
      if (!flags[index]) return flags;
      const next = flags.slice();
      next[index] = false;
      return next;
    });
  }

  function confirmRemove() {
    if (pendingDeleteIndex === null) return;
    const branches = automation.branches.filter((_, i) => i !== pendingDeleteIndex);
    onChange({ ...automation, branches });
    setPristineFlags((flags) => flags.filter((_, i) => i !== pendingDeleteIndex));
    setPendingDeleteIndex(null);
  }

  return (
    <div data-testid="branches-section">
      <SectionCard title="Branches" summary={branchesSummary(automation.branches)} defaultOpen>
        <div className={styles.branchList}>
          {automation.branches.map((branch, index) => (
            <BranchCard
              key={index}
              branch={branch}
              index={index}
              fields={fields}
              lineItemGroups={lineItemGroups}
              lookupTables={lookupTables}
              constants={constants}
              roles={roles}
              users={users}
              classifications={classifications}
              pipelineSteps={pipelineSteps}
              pristine={pristineFlags[index] ?? false}
              removeDisabled={automation.branches.length <= 1}
              onChange={(next) => updateBranch(index, next)}
              onOutcomeCommitted={() => clearPristine(index)}
              onRemove={() => setPendingDeleteIndex(index)}
            />
          ))}

          <button type="button" className={styles.addBranchBtn} onClick={addBranch}>
            + Add another if/then
          </button>

          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              data-testid="stop-after-first-match"
              checked={Boolean(automation.stopAfterFirstMatch)}
              onChange={(e) =>
                onChange({
                  ...automation,
                  stopAfterFirstMatch: e.target.checked ? true : undefined,
                })
              }
            />
            Stop after first match
          </label>
        </div>
      </SectionCard>

      <ConfirmModal
        open={pendingDeleteIndex !== null}
        title="Remove this branch?"
        description="This if/then and its outcomes will be deleted."
        confirmLabel="Remove"
        destructive
        onConfirm={confirmRemove}
        onCancel={() => setPendingDeleteIndex(null)}
      />
    </div>
  );
}
